import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getLatestRoundQuestions, saveQuestionAnswer, getCaseById, consumeCredit, uploadCaseFile } from '../../services/caseService';
import { runFollowUpTriage, runPhase3ReportGeneration } from '../../services/openai/orchestrationService';
import AIProcessingOverlay from '../../components/AIProcessingOverlay';
import { useAuth } from '../../contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';

const ClarifyingQuestionsPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [supplementalFiles, setSupplementalFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => { loadData(); }, [caseId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [caseResult, questionsResult] = await Promise.all([
        getCaseById(caseId),
        getLatestRoundQuestions(caseId),
      ]);
      if (caseResult?.success) setCaseData(caseResult?.data);
      if (questionsResult?.success) {
        setQuestions(questionsResult?.data || []);
        setCurrentRound(questionsResult?.round || 1);
        const pre = {};
        questionsResult?.data?.forEach(q => { if (q?.answer_text) pre[q?.id] = q?.answer_text; });
        setAnswers(pre);
      }
    } catch (err) {
      console.error('Load data error:', err);
      toast?.error('Failed to load clarifying questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // â”€â”€ Supplemental file handling â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/tiff'];

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter(f => ACCEPTED.includes(f.type));
    if (valid.length < incoming.length) toast.error('Only image files are accepted.');
    setSupplementalFiles(prev => [...prev, ...valid]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeSupplementalFile = (idx) =>
    setSupplementalFiles(prev => prev.filter((_, i) => i !== idx));

  // â”€â”€ Submit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSubmit = async () => {
    const requiredQuestions = questions?.filter(q => q?.priority === 'required');
    const unanswered = requiredQuestions?.filter(q => !answers?.[q?.id]?.trim());
    if (unanswered?.length > 0) {
      toast?.error('Please answer all required questions before proceeding');
      return;
    }

    try {
      setIsSubmitting(true);

      // 1. Save text answers
      for (const question of questions) {
        if (answers?.[question?.id]) {
          await saveQuestionAnswer(question?.id, answers?.[question?.id]);
        }
      }

      // 2. Upload any supplemental images
      if (supplementalFiles.length > 0) {
        toast?.dismiss();
        toast?.success(`Uploading ${supplementalFiles.length} supplemental image${supplementalFiles.length > 1 ? 's' : ''}...`);
        await Promise.all(
          supplementalFiles.map(f => uploadCaseFile(user?.id, caseId, f))
        );
      }

      // 3. Run follow-up triage - AI decides: more questions or generate report
      toast?.dismiss();
      toast?.loading('Evaluating answers...', { id: 'eval' });
      const followUp = await runFollowUpTriage(caseId, user?.id);
      toast?.dismiss('eval');

      if (!followUp?.success) {
        toast?.error(followUp?.error || 'Evaluation failed. Please try again.');
        return;
      }

      if (followUp?.needsMoreClarification) {
        toast?.success(`Round ${followUp.round} - the AI has a few more questions.`);
        setSupplementalFiles([]);
        setAnswers({});
        await loadData();   // loads the new round's questions
        return;
      }

      // 4. AI is satisfied - generate report
      toast?.loading('Generating report — this may take a minute...', { id: 'report-gen', duration: 120000 });
      const reportResult = await runPhase3ReportGeneration(caseId, user?.id);
      toast?.dismiss('report-gen');

      if (reportResult?.success) {
        const creditResult = await consumeCredit(user?.id, caseId);
        if (!creditResult?.success) console.error('Credit consumption failed:', creditResult?.error);
        toast?.success('Report generated successfully!');
        navigate(`/report-viewer/${caseId}`);
      } else {
        toast?.error(reportResult?.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast?.error('Failed to submit answers');
    } finally {
      setIsSubmitting(false);
    }
  };

  // â”€â”€ Derived â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const requiredQuestions = questions?.filter(q => q?.priority === 'required');
  const optionalQuestions = questions?.filter(q => q?.priority !== 'required');
  const answeredRequired = requiredQuestions?.filter(q => answers?.[q?.id]?.trim())?.length;
  // True when at least one required question explicitly needs an image upload
  const imageUploadRequired = requiredQuestions?.some(q => q?.needs_image);
  const imagesProvided = supplementalFiles.length > 0;
  const canProceed =
    answeredRequired === requiredQuestions?.length &&
    (!imageUploadRequired || imagesProvided);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading clarifying questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <Sidebar userRole="client" />
      <QuickActionMenu userRole="client" />
      <div className="lg:ml-60 min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl md:text-3xl font-semibold">Clarifying Questions</h1>
                  {currentRound > 1 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-warning/15 text-warning border border-warning/30">
                      Round {currentRound}
                    </span>
                  )}
                </div>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Case #{caseData?.case_number} - {caseData?.property_address}
                </p>
              </div>
              <Button variant="ghost" onClick={() => navigate('/client-dashboard')} iconName="X">
                Cancel
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 space-y-6">

          {/* Info banner */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start gap-3 mb-5">
              <Icon name="AlertCircle" size={22} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-lg mb-1">Additional Information Needed</h3>
                <p className="text-sm text-muted-foreground">
                  The AI has identified areas where more information would improve report accuracy.
                  Answer the required questions below and optionally add supplemental images for any
                  additional evidence you have.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-0.5">Required answered</p>
                <p className="font-semibold">{answeredRequired} / {requiredQuestions?.length}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-0.5">Optional answered</p>
                <p className="font-semibold">
                  {optionalQuestions?.filter(q => answers?.[q?.id]?.trim())?.length} / {optionalQuestions?.length}
                </p>
              </div>
              {currentRound > 1 && (
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-0.5">Clarification round</p>
                  <p className="font-semibold">{currentRound} of 5 max</p>
                </div>
              )}
            </div>
          </div>

          {/* Required questions */}
          {requiredQuestions?.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-2 mb-5">
                <Icon name="AlertTriangle" size={18} className="text-error" />
                <h2 className="text-lg font-semibold">Required Questions</h2>
              </div>
              <div className="space-y-5">
                {requiredQuestions?.map((question, index) => (
                  <div key={question?.id}>
                    <label className="block">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-error/10 text-error flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </span>
                        <span className="font-medium text-foreground">{question?.question_text}</span>
                      </div>
                      <textarea
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        rows="3"
                        placeholder="Enter your answer..."
                        value={answers?.[question?.id] || ''}
                        onChange={(e) => handleAnswerChange(question?.id, e?.target?.value)}
                        disabled={isSubmitting}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional questions */}
          {optionalQuestions?.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-2 mb-5">
                <Icon name="Info" size={18} className="text-primary" />
                <h2 className="text-lg font-semibold">Optional Questions</h2>
                <span className="text-sm text-muted-foreground">(can be skipped)</span>
              </div>
              <div className="space-y-5">
                {optionalQuestions?.map((question, index) => (
                  <div key={question?.id}>
                    <label className="block">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </span>
                        <span className="font-medium text-foreground">{question?.question_text}</span>
                      </div>
                      <textarea
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        rows="3"
                        placeholder="Enter your answer (optional)..."
                        value={answers?.[question?.id] || ''}
                        onChange={(e) => handleAnswerChange(question?.id, e?.target?.value)}
                        disabled={isSubmitting}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Supplemental image upload */}
          <div className={`bg-card rounded-lg border p-6 ${
            imageUploadRequired && !imagesProvided
              ? 'border-error ring-1 ring-error/40'
              : 'border-border'
          }`}>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ImagePlus" size={18} className={imageUploadRequired ? 'text-error' : 'text-primary'} />
              <h2 className="text-lg font-semibold">Supplemental Evidence Images</h2>
              {imageUploadRequired ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-error/10 text-error border border-error/30">
                  Required
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">(optional)</span>
              )}
            </div>
            {imageUploadRequired && (
              <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-error/10 border border-error/20">
                <Icon name="AlertTriangle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error font-medium">
                  The AI requires additional photographic evidence to proceed. Please upload at least one image before submitting.
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground mb-4">
              {imageUploadRequired
                ? 'Upload the requested photos below — they will be analyzed alongside your answers.'
                : 'Have additional photos that help answer the questions above? Upload them here - the AI will incorporate them in the next evaluation.'}
            </p>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : imageUploadRequired && !imagesProvided
                    ? 'border-error/60 bg-error/5 hover:border-error hover:bg-error/10'
                    : 'border-border hover:border-primary/50 hover:bg-muted/40'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
              <Icon name="UploadCloud" size={36} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground mb-1">Drop images here or click to browse</p>
              <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, TIFF - any number of files</p>
            </div>

            {/* File list */}
            {supplementalFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {supplementalFiles.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                    <Icon name="Image" size={16} className="text-primary flex-shrink-0" />
                    <span className="text-sm text-foreground flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeSupplementalFile(idx); }}
                      className="p-1 hover:bg-error/10 rounded text-muted-foreground hover:text-error transition-colors"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Warning */}
          {!canProceed && (
            <div className="p-4 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">
                {answeredRequired < requiredQuestions?.length && imageUploadRequired && !imagesProvided
                  ? `You must answer all ${requiredQuestions?.length} required question${requiredQuestions?.length !== 1 ? 's' : ''} and upload at least one image before proceeding.`
                  : answeredRequired < requiredQuestions?.length
                    ? `You must answer all ${requiredQuestions?.length} required question${requiredQuestions?.length !== 1 ? 's' : ''} before proceeding.`
                    : 'You must upload at least one image before proceeding.'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col md:flex-row gap-3 pb-8">
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={!canProceed || isSubmitting}
              iconName={isSubmitting ? 'Loader' : 'ArrowRight'}
              iconPosition="right"
              fullWidth
            >
              {isSubmitting
                ? 'Processing...'
                : supplementalFiles.length > 0
                  ? `Submit Answers & ${supplementalFiles.length} Image${supplementalFiles.length > 1 ? 's' : ''}`
                  : 'Submit Answers'}
            </Button>
          </div>

        </div>
      </div>

      {/* AI Processing Overlay */}
      <AIProcessingOverlay isVisible={isSubmitting} />
    </div>
  );
};

export default ClarifyingQuestionsPage;
